import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave,
  faHistory,
  faPlus,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faFilter,
  faFileCsv,
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { DepensesContext } from '../layout/CunstomDepenses';

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('CaisseDisplayCache');
db.version(1).stores({ caisse: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedCaisse(key, allowExpired = false) {
  try {
    const entry = await db.caisse.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch {
    return null;
  }
}

async function setCachedCaisse(key, data) {
  await db.caisse.put({ key, data, timestamp: Date.now() });
}

async function getStaleCaisse(key) {
  return getCachedCaisse(key, true);
}

// ============================================================
// CONFIGURATION API
// ============================================================
const API_URL = `${API_CONFIG2.apiUrl}caisse/api_caisse.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const CaisseDisplay = ({ userId }) => {
  const context = useContext(DepensesContext) || {};
  const { refreshKey, setSource } = context;

  // États
  const [caisse, setCaisse] = useState(null);
  const [mouvements, setMouvements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('cache');

  // États pour les filtres et la pagination
  const [filterType, setFilterType] = useState('tous');
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    type: 'Entrée',
    montant: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const toastTimeoutRef = useRef(null);

  // Annulation des requêtes
  const abortControllerRef = useRef(null);

  // ============================================================
  // GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false });
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // CHARGEMENT DE LA CAISSE
  // ============================================================
  const fetchCaisse = useCallback(
    async (forceRefresh = false) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      setError(null);

      const cacheKey = userId ? `caisse_${userId}` : 'caisse_global';

      try {
        if (!forceRefresh) {
          const cached = await getCachedCaisse(cacheKey);
          if (cached) {
            setCaisse(cached.caisse);
            setMouvements(cached.mouvements || []);
            setDataSource('cache');
            if (setSource) setSource('cache');
            setLoading(false);
            return;
          }
        }
        const uniqueParam = new Date().getTime();
        const response = await fetch(`${API_URL}?action=get_caisse&unique=${uniqueParam}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          const stale = await getStaleCaisse(cacheKey);
          if (stale) {
            setCaisse(stale.caisse);
            setMouvements(stale.mouvements || []);
            setDataSource('cache (périmé)');
            if (setSource) setSource('cache (périmé)');
            showToast('Utilisation du cache (données peut-être obsolètes)', 'warning');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.status === 'success') {
          // 🔧 Correction : on s'assure que caisse n'est jamais null
          const caisseData = data.caisse || { solde: 0, derniere_mise_a_jour: new Date().toISOString() };
          setCaisse(caisseData);
          setMouvements(data.mouvements || []);
          const source = forceRefresh ? 'live (forcé)' : 'live';
          setDataSource(source);
          if (setSource) setSource(source);

          await setCachedCaisse(cacheKey, {
            caisse: caisseData,
            mouvements: data.mouvements || [],
          });

          if (forceRefresh) {
            showToast('Données actualisées', 'success');
          }
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        console.error('fetchCaisse error:', err);

        const stale = await getStaleCaisse(cacheKey);
        if (stale) {
          setCaisse(stale.caisse);
          setMouvements(stale.mouvements || []);
          setDataSource('cache (périmé)');
          if (setSource) setSource('cache (périmé)');
          showToast('Erreur réseau – utilisation du cache', 'warning');
        } else {
          setError(err.message);
          showToast(err.message, 'danger');
        }
      } finally {
        setLoading(false);
      }
    },
    [userId, setSource, showToast]
  );

  // ============================================================
  // EFFETS : INITIALISATION ET REFRESH
  // ============================================================
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      fetchCaisse(true);
    }
  }, [refreshKey, fetchCaisse]);

  useEffect(() => {
    fetchCaisse();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, [fetchCaisse]);

  // ============================================================
  // SOUMISSION DU FORMULAIRE
  // ============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const montant = parseFloat(formData.montant);
    if (isNaN(montant) || montant <= 0) {
      showToast('Veuillez saisir un montant valide (> 0)', 'danger');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        type: formData.type,
        montant,
        description: formData.description || `Mouvement ${formData.type === 'Entrée' ? 'entrant' : 'sortant'}`,
        userId,
      };

      const data = await fetchJSON(`${API_URL}?action=ajouter_mouvement`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (data.status === 'success') {
        showToast(data.message, 'success');
        setShowModal(false);
        setFormData({ type: 'Entrée', montant: '', description: '' });

        const cacheKey = userId ? `caisse_${userId}` : 'caisse_global';
        await db.caisse.delete(cacheKey);
        await fetchCaisse(true);
      } else {
        throw new Error(data.message || 'Erreur inconnue');
      }
    } catch (err) {
      showToast(err.message, 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================
  // FONCTIONS DE FILTRAGE ET PAGINATION
  // ============================================================
  const filteredMouvements = mouvements.filter((m) => {
    // Filtre type
    if (filterType !== 'tous' && m.type !== filterType) return false;

    // Filtre dates
    if (filterDateDebut) {
      const dateMvt = new Date(m.date);
      const dateDebut = new Date(filterDateDebut);
      if (dateMvt < dateDebut) return false;
    }
    if (filterDateFin) {
      const dateMvt = new Date(m.date);
      const dateFin = new Date(filterDateFin);
      dateFin.setHours(23, 59, 59, 999);
      if (dateMvt > dateFin) return false;
    }

    // Filtre recherche textuelle
    if (searchText && !m.description?.toLowerCase().includes(searchText.toLowerCase())) {
      return false;
    }

    return true;
  });

  // Trier par date décroissante
  const sortedMouvements = [...filteredMouvements].sort((a, b) => new Date(b.date) - new Date(a.date));

  const totalItems = sortedMouvements.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMouvements = sortedMouvements.slice(startIndex, startIndex + itemsPerPage);

  // Réinitialiser la page lorsque les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, filterDateDebut, filterDateFin, searchText]);

  // ============================================================
  // EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (filteredMouvements.length === 0) {
      showToast('Aucune donnée à exporter.', 'warning');
      return;
    }

    const headers = ['Type', 'Montant', 'Description', 'Solde après', 'Date'];
    const rows = filteredMouvements.map((m) => [
      m.type,
      m.montant.toFixed(2),
      m.description || '',
      m.solde_apres !== null ? m.solde_apres.toFixed(2) : '',
      new Date(m.date).toLocaleString(),
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mouvements_caisse_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && !caisse) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement de la caisse...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5 text-danger">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        {error}
        <div className="mt-3">
          <button className="btn-caisse primary" onClick={() => fetchCaisse(true)}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // 🛡️ GARDE : si caisse est null (après chargement ou erreur)
  // ============================================================
  if (!caisse) {
    return (
      <div className="text-center py-5 text-secondary">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        Aucune donnée de caisse disponible.
        <div className="mt-3">
          <button className="btn-caisse primary" onClick={() => fetchCaisse(true)}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDU PRINCIPAL
  // ============================================================
  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – CAISSE DISPLAY (mobile optimisé)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .caisse-display-wrapper {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 1.5vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: clamp(0.6rem, 0.9vw, 0.75rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .solde-card {
          text-align: center;
        }
        .solde-valeur {
          font-size: clamp(2.2rem, 6vw, 3.5rem);
          font-weight: 700;
          color: var(--accent-blue);
          margin-top: 10px;
        }
        .solde-label {
          color: var(--text-secondary);
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .filter-section {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          align-items: flex-end;
        }
        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1 1 140px;
        }
        .filter-group label {
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          text-transform: uppercase;
          color: var(--text-secondary);
          letter-spacing: 0.5px;
          font-weight: 600;
        }
        .filter-group input,
        .filter-group select {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 8px 14px;
          color: var(--text-primary);
          outline: none;
          font-size: clamp(0.8rem, 1vw, 0.9rem);
          transition: 0.3s;
          min-height: 44px;
          width: 100%;
        }
        .filter-group input:focus,
        .filter-group select:focus {
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
        }
        .filter-group input::placeholder {
          color: var(--text-muted);
        }
        .filter-group select option {
          background: #0b1428;
        }

        .btn-caisse {
          padding: 8px 18px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.75rem, 0.9vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .btn-caisse:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .btn-caisse:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .btn-caisse.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .btn-caisse.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }

        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 10px 14px;
          width: 100%;
          transition: 0.3s;
          font-size: clamp(0.85rem, 1.1vw, 1rem);
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.06);
        }
        .monetra-input option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .table-glass {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
        }
        .table-glass th {
          padding: 10px 8px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .table-glass td {
          padding: 10px 8px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .table-glass tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .badge-type {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: clamp(0.6rem, 0.8vw, 0.7rem);
          font-weight: 600;
        }
        .badge-type.entree {
          background: rgba(59, 130, 246, 0.15);
          color: var(--accent-blue);
        }
        .badge-type.sortie {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 20px;
        }
        .pagination-controls .info {
          color: var(--text-secondary);
          font-size: clamp(0.75rem, 0.9vw, 0.85rem);
        }
        .pagination-controls .buttons {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .pagination-controls .buttons button {
          padding: 6px 14px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: 0.2s;
          cursor: pointer;
          font-size: clamp(0.7rem, 0.9vw, 0.8rem);
          min-height: 36px;
          touch-action: manipulation;
        }
        .pagination-controls .buttons button:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
        }
        .pagination-controls .buttons button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        .pagination-controls .buttons button.active {
          background: var(--accent-blue);
          color: #fff;
          border-color: var(--accent-blue);
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(1rem, 1.5vw, 1.5rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
          padding: clamp(0.8rem, 1.5vw, 1.2rem) clamp(1.2rem, 2vw, 2rem);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }
        .monetra-modal .modal-body {
          padding: clamp(1.2rem, 2vw, 2rem);
        }

        .toast-custom {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
          font-size: clamp(0.85rem, 1.2vw, 1rem);
        }

        .text-secondary {
          color: var(--text-secondary);
        }
        .text-danger {
          color: #ef4444;
        }
        .text-success {
          color: var(--accent-blue);
        }
        .fw-bold {
          font-weight: 600;
        }
        .small {
          font-size: clamp(0.7rem, 0.9vw, 0.85rem);
        }
        .text-center {
          text-align: center;
        }
        .py-5 {
          padding-top: 3rem;
          padding-bottom: 3rem;
        }
        .mt-2 {
          margin-top: 0.5rem;
        }
        .mb-3 {
          margin-bottom: 1rem;
        }
        .mb-4 {
          margin-bottom: clamp(1rem, 2vw, 1.5rem);
        }
        .me-2 {
          margin-right: 0.5rem;
        }
        .ms-auto {
          margin-left: auto;
        }
        .d-flex {
          display: flex;
        }
        .flex-wrap {
          flex-wrap: wrap;
        }
        .align-items-center {
          align-items: center;
        }
        .justify-content-between {
          justify-content: space-between;
        }
        .gap-2 {
          gap: 0.5rem;
        }

        /* ============================================================
           RESPONSIVE MOBILE
           ============================================================ */
        @media (max-width: 768px) {
          .caisse-display-wrapper { padding: 12px; }
          .glass-card { padding: 1rem; }

          .solde-valeur {
            font-size: 2.2rem;
          }

          .filter-section {
            flex-direction: column;
            align-items: stretch;
          }
          .filter-group {
            flex: 1 1 auto;
            width: 100%;
          }
          .filter-group input,
          .filter-group select {
            min-height: 40px;
            font-size: 0.9rem;
          }

          .btn-caisse {
            font-size: 0.8rem;
            padding: 6px 14px;
            min-height: 40px;
            justify-content: center;
          }

          .table-glass th,
          .table-glass td {
            padding: 6px 6px;
            font-size: 0.7rem;
          }

          .pagination-controls {
            flex-direction: column;
            align-items: center;
          }
          .pagination-controls .buttons {
            justify-content: center;
          }
          .pagination-controls .buttons button {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 32px;
          }

          .monetra-modal .modal-footer {
            flex-direction: column-reverse;
          }
          .monetra-modal .modal-footer button {
            width: 100%;
            justify-content: center;
          }
        }
        @media (max-width: 480px) {
          .solde-valeur {
            font-size: 1.8rem;
          }
          .btn-caisse {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 36px;
          }
          .table-glass th,
          .table-glass td {
            font-size: 0.65rem;
            padding: 4px 4px;
          }
          .filter-group input,
          .filter-group select {
            font-size: 0.8rem;
            min-height: 36px;
          }
        }
      `}</style>

      <div className="caisse-display-wrapper">
        {/* En-tête avec badge source */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <h2 className="fw-bold mb-0" style={{ fontSize: 'clamp(1.4rem, 4vw, 2rem)' }}>
            <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" style={{ color: 'var(--accent-blue)' }} />
            Caisse
          </h2>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <span
              className={`badge-source ${
                dataSource === 'live' || dataSource === 'live (forcé)'
                  ? 'live'
                  : dataSource === 'cache (périmé)'
                  ? 'warning'
                  : ''
              }`}
            >
              {dataSource === 'cache'
                ? '📦 Cache'
                : dataSource === 'cache (périmé)'
                ? '⚠️ Cache périmé'
                : dataSource === 'live'
                ? '🔄 Live'
                : dataSource === 'live (forcé)'
                ? '🔄 Forcé'
                : '📦 Cache'}
            </span>
            <button className="btn-caisse primary" onClick={() => fetchCaisse(true)}>
              <FontAwesomeIcon icon={faClock} /> Rafraîchir
            </button>
          </div>
        </div>

        {/* Solde */}
        <div className="glass-card solde-card">
          <div className="solde-label">Solde actuel</div>
          <div className="solde-valeur">{caisse.solde.toFixed(2)} USD</div>
          <div className="text-secondary small mt-2">
            Dernière mise à jour :{' '}
            {new Date(caisse.derniere_mise_a_jour).toLocaleString()}
          </div>
          <div className="mt-3">
            <button className="btn-caisse primary" onClick={() => setShowModal(true)}>
              <FontAwesomeIcon icon={faPlus} /> Ajouter un mouvement
            </button>
          </div>
        </div>

        {/* Filtres et export */}
        <div className="glass-card">
          <h5 className="fw-bold mb-3" style={{ color: 'var(--accent-blue)', fontSize: 'clamp(1rem, 1.4vw, 1.2rem)' }}>
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Filtrer les mouvements
          </h5>
          <div className="filter-section">
            <div className="filter-group">
              <label>Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="tous">Tous</option>
                <option value="Entrée">Entrée</option>
                <option value="Sortie">Sortie</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Date début</label>
              <input
                type="date"
                value={filterDateDebut}
                onChange={(e) => setFilterDateDebut(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>Date fin</label>
              <input
                type="date"
                value={filterDateFin}
                onChange={(e) => setFilterDateFin(e.target.value)}
              />
            </div>
            <div className="filter-group" style={{ flex: 2 }}>
              <label>Recherche</label>
              <input
                type="text"
                placeholder="Rechercher dans la description..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
            <div className="filter-group" style={{ flex: 0 }}>
              <button className="btn-caisse" onClick={exportCSV}>
                <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
              </button>
            </div>
          </div>
        </div>

        {/* Liste des mouvements avec pagination */}
        <div className="glass-card">
          <h5 className="fw-bold mb-3" style={{ color: 'var(--accent-blue)', fontSize: 'clamp(1rem, 1.4vw, 1.2rem)' }}>
            <FontAwesomeIcon icon={faHistory} className="me-2" />
            Mouvements
            <span className="text-secondary small ms-2">
              ({filteredMouvements.length} enregistrements)
            </span>
          </h5>
          {paginatedMouvements.length === 0 ? (
            <p className="text-secondary">Aucun mouvement correspondant aux filtres.</p>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table-glass">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Montant</th>
                      <th>Description</th>
                      <th>Solde après</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMouvements.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <span className={`badge-type ${m.type.toLowerCase()}`}>
                            {m.type}
                          </span>
                        </td>
                        <td className={m.type === 'Entrée' ? 'text-success' : 'text-danger'}>
                          {m.type === 'Entrée' ? '+' : '-'}
                          {m.montant.toFixed(2)} USD</td>
                        <td>{m.description || '-'}</td>
                        <td>
                          {m.solde_apres !== null
                            ? m.solde_apres.toFixed(2) + 'USD'
                            : '-'}
                        </td>
                        <td className="text-secondary small">
                          {new Date(m.date).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="info">
                    Page {currentPage} sur {totalPages} ({totalItems} éléments)
                  </div>
                  <div className="buttons">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      Précédent
                    </button>
                    {[...Array(Math.min(totalPages, 5)).keys()].map((num) => {
                      let pageNum = num + 1;
                      if (totalPages > 5) {
                        if (currentPage <= 3) {
                          pageNum = num + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + num;
                        } else {
                          pageNum = currentPage - 2 + num;
                        }
                      }
                      return (
                        <button
                          key={pageNum}
                          className={currentPage === pageNum ? 'active' : ''}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal Ajout mouvement */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        className="monetra-modal"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold" style={{ fontSize: 'clamp(1.1rem, 2vw, 1.3rem)' }}>
              <FontAwesomeIcon icon={faPlus} className="text-success me-2" />
              Ajouter un mouvement
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small">Type</Form.Label>
              <Form.Select
                name="type"
                value={formData.type}
                onChange={(e) =>
                  setFormData({ ...formData, type: e.target.value })
                }
                className="monetra-input"
              >
                <option value="Entrée">Entrée (encaissement)</option>
                <option value="Sortie">Sortie (décaissement)</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small">
                Montant ($)
              </Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0.01"
                value={formData.montant}
                onChange={(e) =>
                  setFormData({ ...formData, montant: e.target.value })
                }
                required
                className="monetra-input text-danger"
                placeholder="0.00"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small">
                Description (optionnel)
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="monetra-input text-danger"
                placeholder="Motif du mouvement"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={() => setShowModal(false)}
              className="btn-caisse"
            >
              Annuler
            </Button>
            <Button
              variant="success"
              type="submit"
              className="btn-caisse primary"
              disabled={submitting}
            >
              {submitting ? (
                <Spinner as="span" animation="border" size="sm" />
              ) : (
                'Enregistrer'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Toast */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
        <div
          className={`toast-custom p-3 ${toast.show ? 'd-block' : 'd-none'}`}
          style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}
        >
          <div className="d-flex align-items-center gap-3">
            <FontAwesomeIcon
              icon={
                toast.variant === 'success'
                  ? faCheckCircle
                  : faExclamationTriangle
              }
              className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
            />
            <span>{toast.message}</span>
            <button
              className="btn-close-custom ms-auto"
              onClick={() => setToast({ ...toast, show: false })}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-primary)',
                opacity: 0.5,
                fontSize: '1.2rem',
              }}
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CaisseDisplay;